import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, Navigate } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import { SEO_BASE_URL } from '../constants/seo';
import {
  getBestToolConfig,
  getBestToolCanonicalUrl,
  BEST_TOOL_TOPICS,
  type BestToolTopic,
} from '../data/bestToolSEOPages';

const BestToolSEOPage: React.FC = () => {
  const { topic } = useParams<{ topic: string }>();
  const topicSlug = topic ? `best-${topic}` : '';
  const config = useMemo(() => (topicSlug ? getBestToolConfig(topicSlug) : null), [topicSlug]);

  if (!config) {
    return <Navigate to="/tools" replace />;
  }

  const canonicalUrl = getBestToolCanonicalUrl(config.topic as BestToolTopic);
  const otherBestPages = BEST_TOOL_TOPICS.filter((t) => t !== config.topic);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={config.ogTitle} />
        <meta property="og:description" content={config.ogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SEO_BASE_URL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${SEO_BASE_URL}/og-image.jpg`} />
        <meta name="twitter:title" content={config.ogTitle} />
        <meta name="twitter:description" content={config.ogDescription} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#FFF8F0]">
        <PublicHeader />

        <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <article>
            <header className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                {config.h1}
              </h1>
              <p className="text-lg text-gray-600 mt-2">{config.subtitle}</p>
            </header>

            <section className="prose prose-gray max-w-none mb-10">
              <p className="text-gray-700 leading-relaxed">{config.intro}</p>
            </section>

            <section className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Why use this tool</h2>
              <ul className="space-y-2 text-gray-700">
                {config.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-10 flex flex-wrap gap-4 items-center">
              <Link
                to={config.ctaHref}
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-white bg-orange-500 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 transition-colors"
              >
                {config.ctaText}
              </Link>
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-orange-600 bg-orange-100 hover:bg-orange-200 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 transition-colors"
              >
                Start Focus Session
              </Link>
            </section>

            <section className="mb-10 pt-8 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
              <dl className="space-y-4">
                {config.faqs.map((faq, i) => (
                  <div key={i}>
                    <dt className="font-medium text-gray-900">{faq.question}</dt>
                    <dd className="mt-1 text-gray-600 pl-0">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="pt-8 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">More free tools</h2>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <Link to="/tools" className="text-orange-600 hover:text-orange-700 hover:underline">
                    All free productivity tools
                  </Link>
                </li>
                {otherBestPages.map((t) => (
                  <li key={t}>
                    <Link
                      to={`/tools/${t}`}
                      className="text-orange-600 hover:text-orange-700 hover:underline"
                    >
                      Best {t.replace('best-', '').replace(/-/g, ' ')}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </article>
        </main>
      </div>
    </>
  );
};

export default BestToolSEOPage;
